/**
 * 增强版 Shopify Storefront API 封装类
 * 支持缓存、重试、批量操作、请求管理等高级功能
 *  this.config = {
      shopDomain: "kate-background.myshopify.com",
      apiVersion: "2025-01",
      storefrontAccessToken: "7ede779d0c2e7d991c95d50b99084310",
      ...options,
    };
 */
class StorefrontAPI {
  constructor(options = {}) {
    this.config = {
      shopDomain: "kate-background.myshopify.com",
      apiVersion: "2025-01",
      storefrontAccessToken: "7ede779d0c2e7d991c95d50b99084310",
      ...options,
    };

    // 内置配置参数，不需要外部传递
    this.options = {
      cacheTimeout: 5 * 60 * 1000, // 5分钟缓存
      maxRetries: 3,
      retryDelay: 1000,
      requestTimeout: 30000,
    };

    this.cache = new Map();
    this.requestQueue = new Map();
    this.activeRequests = new Set();
  }

  /**
   * 核心 GraphQL 请求方法
   */
  async shopifyFetch(query, variables = {}, options = {}) {
    const { useCache = true, cacheKey = null, tokenless = false } = options;

    // 构建缓存键
    const finalCacheKey = `${tokenless ? 'online-store:' : ''}${cacheKey || this._buildCacheKey(query, variables)}`;

    // 检查缓存
    if (useCache && this.cache.has(finalCacheKey)) {
      const cached = this.cache.get(finalCacheKey);
      if (Date.now() - cached.timestamp < this.options.cacheTimeout) {
        // console.log("从缓存获取 GraphQL 数据");
        return cached.data;
      }
      this.cache.delete(finalCacheKey);
    }

    // 检查是否已有相同请求在进行
    if (this.requestQueue.has(finalCacheKey)) {
      return await this.requestQueue.get(finalCacheKey);
    }

    // 创建新请求
    const requestPromise = this._makeGraphQLRequest(query, variables, { tokenless });
    this.requestQueue.set(finalCacheKey, requestPromise);

    try {
      const result = await requestPromise;

      // 缓存结果
      if (useCache) {
        this.cache.set(finalCacheKey, {
          data: result,
          timestamp: Date.now(),
        });
      }

      return result;
    } finally {
      this.requestQueue.delete(finalCacheKey);
    }
  }

  /**
   * 内部方法：发送 GraphQL 请求
   */
  async _makeGraphQLRequest(query, variables, { tokenless = false } = {}) {
    const endpoint = `https://${this.config.shopDomain}/api/${tokenless ? '2026-07' : this.config.apiVersion}/graphql.json`;

    // 为每个请求创建独立的AbortController
    const abortController = new AbortController();
    this.activeRequests.add(abortController);

    const requestOptions = {
      method: "POST",
      headers: {
        ...(tokenless ? {} : { "X-Shopify-Storefront-Access-Token": this.config.storefrontAccessToken }),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ query, variables }),
      signal: abortController.signal,
    };

    let lastError;

    try {
      for (let attempt = 1; attempt <= this.options.maxRetries; attempt++) {
        try {
          const response = await Promise.race([
            fetch(endpoint, requestOptions),
            new Promise((_, reject) => {
              setTimeout(() => reject(new Error(`GraphQL 请求超时 (${this.options.requestTimeout / 1000}秒)`)), this.options.requestTimeout);
            }),
          ]);

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const resJson = await response.json();

          if (resJson.errors) {
            console.error("Shopify GraphQL Error:", resJson.errors);
            throw new Error(resJson.errors[0].message);
          }

          return resJson.data;
        } catch (error) {
          lastError = error;

          // 如果是AbortError，直接抛出，不重试
          if (error.name === "AbortError") {
            throw error;
          }

          if (attempt < this.options.maxRetries) {
            console.warn(`GraphQL 请求失败，第 ${attempt} 次重试:`, error.message);
            await new Promise((resolve) => setTimeout(resolve, this.options.retryDelay * attempt));
          }
        }
      }

      throw lastError;
    } finally {
      this.activeRequests.delete(abortController);
    }
  }

  /**
   * 映射 sortBy 到 GraphQL sortKey
   */
  mapSortKey(sortBy) {
    const sortKeyMap = {
      manual: "MANUAL",
      "best-selling": "BEST_SELLING",
      "title-ascending": "TITLE",
      "title-descending": "TITLE",
      "price-ascending": "PRICE",
      "price-descending": "PRICE",
      "created-ascending": "CREATED",
      "created-descending": "CREATED",
      relevance: "RELEVANCE",
    };
    return sortKeyMap[sortBy] || "MANUAL";
  }

  /**
   * 获取集合产品列表（分页、排序）
   */
  async getCollectionProducts(handle, options = {}) {
    const {
      limit = 10,
      cursor = null,
      sortKey = "MANUAL",
      sortBy = null, // 支持旧的 sortBy 参数
      reverse = false,
      useCache = true,
    } = options;

    // 如果提供了 sortBy，转换为 sortKey
    const finalSortKey = sortBy ? this.mapSortKey(sortBy) : sortKey;

    const query = `
      query ($handle: String!, $limit: Int!, $cursor: String, $sortKey: ProductCollectionSortKeys, $reverse: Boolean!) {
        collectionByHandle(handle: $handle) {
          id
          title
          products(first: $limit, after: $cursor, sortKey: $sortKey, reverse: $reverse) {
            edges {
              cursor
              node {
                id
                title
                handle
                description
                productType
                images(first: 1) {
                  edges {
                    node {
                      url
                    }
                  }
                }
                variants(first: 5) {
                  edges {
                    node {
                      id
                      sku
                      price {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
            pageInfo {
              hasNextPage
              hasPreviousPage
            }
          }
        }
      }
    `;

    const result = await this.shopifyFetch(query, { handle, limit, cursor, sortKey: finalSortKey, reverse }, { useCache });

    // 转换数据格式以保持与 product-api-wrapper.js 的兼容性
    const collection = result.collectionByHandle;
    if (!collection) {
      return {
        products: [],
        collectionHandle: handle,
        hasNextPage: false,
        hasPreviousPage: false,
        total: 0,
      };
    }

    const edges = collection.products.edges;
    const products = edges.map((edge) => ({
      id: edge.node.id,
      title: edge.node.title,
      handle: edge.node.handle,
      description: edge.node.description,
      productType: edge.node.productType, // 添加产品类型字段
      image: edge.node.images.edges[0]?.node.url,
      cursor: edge.cursor, // 添加 cursor 信息
      variants: edge.node.variants.edges.map((variantEdge) => ({
        id: variantEdge.node.id,
        sku: variantEdge.node.sku,
        price: variantEdge.node.price.amount,
        currency: variantEdge.node.price.currencyCode,
      })),
    }));

    // 计算 nextCursor 和 previousCursor
    const lastEdge = edges[edges.length - 1];
    const firstEdge = edges[0];
    const nextCursor = collection.products.pageInfo.hasNextPage && lastEdge ? lastEdge.cursor : null;
    const previousCursor = collection.products.pageInfo.hasPreviousPage && firstEdge ? firstEdge.cursor : null;

    return {
      products,
      collectionHandle: handle,
      hasNextPage: collection.products.pageInfo.hasNextPage,
      hasPreviousPage: collection.products.pageInfo.hasPreviousPage,
      nextCursor,
      previousCursor,
      total: products.length,
    };
  }

  /**
   * 搜索产品（支持 title, handle, sku）
   */
  async searchProducts(searchQuery, options = {}) {
    const { limit = 10, cursor = null, useCache = true } = options;

    const query = `
      query ($query: String!, $limit: Int!, $cursor: String) {
        products(first: $limit, after: $cursor, query: $query, sortKey: RELEVANCE) {
          edges {
            cursor
              node {
                id
                title
                handle
                description
                productType
                images(first: 1) {
                edges {
                  node {
                    url
                  }
                }
              }
              variants(first: 5) {
                edges {
                  node {
                    id
                    sku
                    price {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `;

    const result = await this.shopifyFetch(query, { query: searchQuery, limit, cursor }, { useCache });

    const edges = result.products.edges;
    const products = edges.map((edge) => ({
      id: edge.node.id,
      title: edge.node.title,
      handle: edge.node.handle,
      description: edge.node.description,
      productType: edge.node.productType,
      image: edge.node.images.edges[0]?.node.url,
      cursor: edge.cursor, // 添加 cursor 信息
      variants: edge.node.variants.edges.map((variantEdge) => ({
        id: variantEdge.node.id,
        sku: variantEdge.node.sku,
        price: variantEdge.node.price.amount,
        currency: variantEdge.node.price.currencyCode,
      })),
    }));

    // 计算 nextCursor 和 previousCursor
    const lastEdge = edges[edges.length - 1];
    const firstEdge = edges[0];
    const nextCursor = result.products.pageInfo.hasNextPage && lastEdge ? lastEdge.cursor : null;
    const previousCursor = result.products.pageInfo.hasPreviousPage && firstEdge ? firstEdge.cursor : null;

    return {
      products,
      query: searchQuery,
      hasNextPage: result.products.pageInfo.hasNextPage,
      hasPreviousPage: result.products.pageInfo.hasPreviousPage,
      nextCursor,
      previousCursor,
      totalResults: products.length,
    };
  }

  /**
   * 获取单个产品详情
   */
  async getProduct(handle, options = {}) {
    const { useCache = true, imageLimit = 5 } = options;

    const query = `
      query ($handle: String!, $imageLimit: Int!) {
        product(handle: $handle) {
          id
          title
          description
          handle
          images(first: $imageLimit) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                sku
                title
                price {
                  amount
                  currencyCode
                }
                availableForSale
                quantityAvailable
              }
            }
          }
        }
      }
    `;

    const result = await this.shopifyFetch(query, { handle, imageLimit }, { useCache });

    const product = result.product;
    if (!product) {
      throw new Error(`产品未找到: ${handle}`);
    }

    return {
      id: product.id,
      title: product.title,
      description: product.description,
      handle: product.handle,
      images: product.images.edges.map((edge) => ({
        url: edge.node.url,
        alt: edge.node.altText,
      })),
      variants: product.variants.edges.map((edge) => ({
        id: edge.node.id,
        sku: edge.node.sku,
        title: edge.node.title,
        price: edge.node.price.amount,
        currency: edge.node.price.currencyCode,
        available: edge.node.availableForSale,
        quantityAvailable: edge.node.quantityAvailable,
      })),
    };
  }

  _normalizeVariantId(value) {
    const normalized = String(value || "").trim();
    if (/^\d+$/.test(normalized)) return `gid://shopify/ProductVariant/${normalized}`;
    if (/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(normalized)) return normalized;
    return "";
  }

  _getProductStorageKey(handle) {
    const normalizedHandle = String(handle || "").trim().toLowerCase();
    return normalizedHandle ? `kate-3d-product-v1:${normalizedHandle}` : "";
  }

  _getVariantStorageKey(variantId) {
    const normalizedId = this._normalizeVariantId(variantId);
    const numericId = normalizedId.match(/(\d+)$/)?.[1];
    return numericId ? `kate-3d-variant-v1:${numericId}` : "";
  }

  _removePersistentCacheEntry(storageKey) {
    if (!storageKey) return;
    try {
      sessionStorage.removeItem(storageKey);
    } catch (error) {
      // Storage can be unavailable; requests will continue through the normal API path.
    }
  }

  _readPersistentProduct(handle) {
    const normalizedHandle = String(handle || "").trim().toLowerCase();
    const storageKey = this._getProductStorageKey(normalizedHandle);
    if (!storageKey) return null;

    try {
      const cached = JSON.parse(sessionStorage.getItem(storageKey));
      if (
        cached?.timestamp
        && Date.now() - cached.timestamp < this.options.cacheTimeout
        && String(cached.product?.handle || "").trim().toLowerCase() === normalizedHandle
        && Array.isArray(cached.product.images)
        && Array.isArray(cached.product.variants)
      ) {
        return cached.product;
      }
    } catch (error) {
      // Invalid or inaccessible storage falls through to the normal API request.
    }

    this._removePersistentCacheEntry(storageKey);
    return null;
  }

  _writePersistentProduct(product, timestamp = Date.now()) {
    const normalizedHandle = String(product?.handle || "").trim().toLowerCase();
    const storageKey = this._getProductStorageKey(normalizedHandle);
    if (!storageKey || !Array.isArray(product?.images) || !Array.isArray(product?.variants)) return;

    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        timestamp,
        product: {
          ...product,
          handle: normalizedHandle,
        },
      }));
    } catch (error) {
      // Storage can be unavailable or full; the normal in-memory cache still works.
    }
  }

  _writePersistentVariant(handle, selectedVariant, timestamp = Date.now()) {
    const normalizedHandle = String(handle || "").trim().toLowerCase();
    const normalizedId = this._normalizeVariantId(selectedVariant?.id);
    const storageKey = this._getVariantStorageKey(normalizedId);
    if (!storageKey || !normalizedHandle || !normalizedId) return;

    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        timestamp,
        handle: normalizedHandle,
        selectedVariant: {
          ...selectedVariant,
          id: normalizedId,
        },
      }));
    } catch (error) {
      // Storage can be unavailable or full; requests will continue through the normal API path.
    }
  }

  _applyVariantSelection(product, selectedVariant) {
    const normalizedId = this._normalizeVariantId(selectedVariant?.id);
    if (!product?.handle || !normalizedId) return null;

    const variants = Array.isArray(product.variants)
      ? product.variants.map((variant) => ({ ...variant }))
      : [];
    const variantIndex = variants.findIndex(
      (variant) => this._normalizeVariantId(variant.id) === normalizedId
    );
    const normalizedVariant = {
      ...(variantIndex >= 0 ? variants[variantIndex] : {}),
      ...selectedVariant,
      id: normalizedId,
    };
    if (variantIndex >= 0) {
      variants[variantIndex] = normalizedVariant;
    } else {
      variants.unshift(normalizedVariant);
    }

    const productImages = Array.isArray(product.images)
      ? product.images.map((image) => ({ ...image }))
      : [];
    const preferredImage = normalizedVariant.image?.url ? normalizedVariant.image : productImages[0] || null;
    const getImageKey = (image) => String(image?.url || "").split("?")[0];
    const images = [preferredImage, ...productImages]
      .filter((image) => image?.url)
      .filter((image, index, list) => (
        list.findIndex((candidate) => getImageKey(candidate) === getImageKey(image)) === index
      ));

    return {
      ...product,
      image: preferredImage?.url || product.image || "",
      images,
      variants,
      selectedVariantId: normalizedId,
      selectedVariant: normalizedVariant,
    };
  }

  _readPersistentVariantProduct(variantId) {
    const normalizedId = this._normalizeVariantId(variantId);
    const storageKey = this._getVariantStorageKey(normalizedId);
    if (!storageKey) return null;

    try {
      const cached = JSON.parse(sessionStorage.getItem(storageKey));
      const cachedVariantId = this._normalizeVariantId(cached?.selectedVariant?.id);
      if (
        cached?.timestamp
        && Date.now() - cached.timestamp < this.options.cacheTimeout
        && cachedVariantId === normalizedId
        && cached.handle
      ) {
        const product = this._readPersistentProduct(cached.handle);
        if (product) return this._applyVariantSelection(product, cached.selectedVariant);
      }
    } catch (error) {
      // Invalid or inaccessible storage falls through to the normal API request.
    }

    this._removePersistentCacheEntry(storageKey);
    return null;
  }

  /**
   * 获取产品完整图库
   */
  async getProductImages(handle, options = {}) {
    const normalizedHandle = String(handle || "").trim().toLowerCase();

    if (normalizedHandle && options.usePersistentCache !== false) {
      const cachedProduct = this._readPersistentProduct(normalizedHandle);
      if (cachedProduct) return cachedProduct;
    }

    const product = await this.getProduct(normalizedHandle, { ...options, imageLimit: 50 });

    if (normalizedHandle && options.usePersistentCache !== false) {
      this._writePersistentProduct(product);
    }

    return product;
  }

  /**
   * 通过变体 ID 批量获取商品、命中变体和对应媒体。
   * 返回值顺序与传入 ID 一致；找不到或不是 ProductVariant 的位置返回 null。
   */
  async getProductsByVariantIds(variantIds, options = {}) {
    const { useCache = true, usePersistentCache = true, imageLimit = 50 } = options;
    const ids = (Array.isArray(variantIds) ? variantIds : [])
      .map((value) => this._normalizeVariantId(value))
      .filter(Boolean);

    if (!ids.length) return [];

    const products = new Array(ids.length).fill(null);
    const missingRequests = [];
    ids.forEach((id, index) => {
      const cachedProduct = usePersistentCache ? this._readPersistentVariantProduct(id) : null;
      if (cachedProduct) {
        products[index] = cachedProduct;
      } else {
        missingRequests.push({ id, index });
      }
    });

    if (!missingRequests.length) return products;

    const query = `
      query ($ids: [ID!]!, $imageLimit: Int!) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            sku
            title
            availableForSale
            quantityAvailable
            price {
              amount
              currencyCode
            }
            image {
              url
              altText
              width
              height
            }
            product {
              id
              title
              description
              handle
              images(first: $imageLimit) {
                edges {
                  node {
                    url
                    altText
                    width
                    height
                  }
                }
              }
              variants(first: 100) {
                edges {
                  node {
                    id
                    sku
                    title
                    availableForSale
                    quantityAvailable
                    price {
                      amount
                      currencyCode
                    }
                    image {
                      url
                      altText
                      width
                      height
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const result = await this.shopifyFetch(
      query,
      { ids: missingRequests.map((request) => request.id), imageLimit },
      { useCache }
    );
    const fetchedProducts = (result.nodes || []).map((variantNode) => {
      if (!variantNode?.product) return null;

      const normalizeImage = (image) => image?.url ? {
        url: image.url,
        alt: image.altText || variantNode.product.title || "",
        width: image.width || null,
        height: image.height || null,
      } : null;
      const productImages = variantNode.product.images.edges
        .map((edge) => normalizeImage(edge.node))
        .filter(Boolean);
      const normalizeVariant = (node) => ({
        id: node.id,
        sku: node.sku,
        title: node.title,
        price: node.price.amount,
        currency: node.price.currencyCode,
        available: node.availableForSale,
        quantityAvailable: node.quantityAvailable,
        image: normalizeImage(node.image),
      });
      const selectedVariant = normalizeVariant(variantNode);
      const variants = variantNode.product.variants.edges.map((edge) => normalizeVariant(edge.node));

      if (!variants.some((variant) => variant.id === selectedVariant.id)) variants.unshift(selectedVariant);

      const baseProduct = {
        id: variantNode.product.id,
        title: variantNode.product.title,
        description: variantNode.product.description,
        handle: variantNode.product.handle,
        image: productImages[0]?.url || "",
        images: productImages,
        variants,
      };

      if (usePersistentCache) {
        const timestamp = Date.now();
        this._writePersistentProduct(baseProduct, timestamp);
        this._writePersistentVariant(baseProduct.handle, selectedVariant, timestamp);
      }

      return this._applyVariantSelection(baseProduct, selectedVariant);
    });

    missingRequests.forEach((request, resultIndex) => {
      products[request.index] = fetchedProducts[resultIndex] || null;
    });

    return products;
  }

  /**
   * 批量获取产品
   */
  async getBatchProducts(productHandles, options = {}) {
    const { concurrency = 5, useCache = true } = options;
    const results = [];

    // 分批处理，避免同时发送过多请求
    for (let i = 0; i < productHandles.length; i += concurrency) {
      const batch = productHandles.slice(i, i + concurrency);
      const batchPromises = batch.map((handle) =>
        this.getProduct(handle, { useCache }).catch((error) => {
          console.error(`批量获取产品失败: ${handle}`, error);
          return null;
        })
      );

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults.filter(Boolean));

      // 添加延迟避免请求过快
      if (i + concurrency < productHandles.length) {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    }

    return results;
  }

  /**
   * 获取集合的所有产品（自动分页）
   */
  async getAllCollectionProducts(collectionHandle, options = {}) {
    const { maxPages = 100, useCache = true, ...queryOptions } = options;
    const allProducts = [];
    let cursor = null;
    let pageCount = 0;

    while (pageCount < maxPages) {
      try {
        const result = await this.getCollectionProducts(collectionHandle, {
          ...queryOptions,
          cursor,
          useCache,
        });

        allProducts.push(...result.products);

        // 如果没有下一页，结束循环
        if (!result.hasNextPage) {
          break;
        }

        // 获取最后一个产品的 cursor
        cursor = result.products[result.products.length - 1]?.cursor;
        pageCount++;
      } catch (error) {
        console.error(`获取第 ${pageCount + 1} 页失败:`, error);
        break;
      }
    }

    return allProducts;
  }

  /**
   * 创建购物车（用于部分结账）
   * @param {Array} lines - 商品行数组，格式: [{ merchandiseId: 'gid://shopify/ProductVariant/xxx', quantity: 1 }]
   * @param {Array} attributes - 可选的购物车属性，格式: [{ key: 'xxx', value: 'xxx' }]
   * @param {Object} options - 可选配置
   * @returns {Promise<Object>} 返回包含购物车 ID 和结账 URL 的对象
   */
  async createCart(lines, attributes = [], options = {}) {
    const { useCache = false } = options; // 创建购物车操作默认不使用缓存

    const query = `
      mutation CreateCart($lines: [CartLineInput!]!, $attributes: [AttributeInput!], $note: String, $discountCodes: [String!]) {
        cartCreate(input: { lines: $lines, attributes: $attributes, note: $note, discountCodes: $discountCodes }) {
          cart { 
            id 
            checkoutUrl
            totalQuantity
            lines(first: 250) {
              nodes {
                quantity
                attributes { key value }
                merchandise { ... on ProductVariant { id } }
                cost { totalAmount { amount currencyCode } }
              }
            }
            cost {
              totalAmount {
                amount
                currencyCode
              }
              subtotalAmount {
                amount
                currencyCode
              }
            }
          }
          userErrors { 
            field 
            message 
          }
        }
      }
    `;

    const variables = {
      lines,
      attributes,
      note: options.note ?? null,
      discountCodes: options.discountCodes ?? null,
    };

    const result = await this.shopifyFetch(query, variables, { useCache, tokenless: lines.some(line => line.attributes?.some(attribute => attribute.key === '_mirror_quote')) });

    // 检查用户错误
    if (result.cartCreate.userErrors && result.cartCreate.userErrors.length > 0) {
      const errorMsg = result.cartCreate.userErrors[0].message;
      throw new Error(errorMsg);
    }

    const cart = result.cartCreate.cart;
    if (!cart) {
      throw new Error("创建购物车失败");
    }

    return {
      cartId: cart.id,
      cartLines: cart.lines?.nodes || [],
      checkoutUrl: cart.checkoutUrl,
      totalQuantity: cart.totalQuantity,
      totalAmount: cart.cost?.totalAmount?.amount,
      subtotalAmount: cart.cost?.subtotalAmount?.amount,
      currencyCode: cart.cost?.totalAmount?.currencyCode,
    };
  }

  /**
   * 创建部分结账（便捷方法）
   * 从购物车中选中的商品创建新的结账流程
   * @param {Array} selectedItems - 选中的商品信息数组，格式: [{ variantId: 'xxx', quantity: 1, properties: {}, sellingPlanId: 'xxx' }]
   * @param {String} originalCartId - 原始购物车ID（可选）
   * @param {Object} trackingParams - 追踪参数对象（可选），格式: { utm_source: 'xxx', utm_medium: 'xxx', ... }
   * @returns {Promise<String>} 返回结账 URL（已附加追踪参数）
   */
  async createPartialCheckout(selectedItems, originalCartId = null, trackingParams = {}, checkoutContext = {}) {
    // 将 variantId 转换为 merchandiseId 格式
    const lines = selectedItems.map((item) => {
      const line = {
        merchandiseId: item.variantId.startsWith("gid://") ? item.variantId : `gid://shopify/ProductVariant/${item.variantId}`,
        quantity: item.quantity,
      };
      const properties = item.attributes || item.properties;
      if (properties) {
        const attributes = Array.isArray(properties)
          ? properties
              .filter((attribute) => attribute && attribute.key != null && attribute.value != null && attribute.value !== "")
              .map((attribute) => ({ key: String(attribute.key), value: String(attribute.value) }))
          : Object.entries(properties)
              .filter(([, value]) => value != null && value !== "")
              .map(([key, value]) => ({ key: String(key), value: String(value) }));
        if (attributes.length > 0) {
          line.attributes = attributes;
        }
      }
      const sellingPlanId = item.sellingPlanId || item.selling_plan;
      if (sellingPlanId) {
        line.sellingPlanId = sellingPlanId.startsWith("gid://") ? sellingPlanId : `gid://shopify/SellingPlan/${sellingPlanId}`;
      }
      return line;
    });

    // 构建属性
    const attributes = Object.entries(checkoutContext.attributes || {})
      .filter(([, value]) => value != null && value !== '')
      .map(([key, value]) => ({ key, value: String(value) }));
    attributes.push({ key: "source", value: "partial-checkout" });

    if (originalCartId) {
      attributes.push({ key: "original_cart_id", value: originalCartId });
    }

    // 将追踪参数添加到购物车属性中（用于订单备注）
    Object.entries(trackingParams).forEach(([key, value]) => {
      if (value != null && value !== '') {
        attributes.push({ key: key, value: String(value) });
      }
    });

    // 调用创建购物车方法
    const result = await this.createCart(lines, attributes, { note: checkoutContext.note, discountCodes: checkoutContext.discountCodes });
    if (checkoutContext.expectedGift) {
      const expected = checkoutContext.expectedGift;
      const gifts = result.cartLines.filter((line) => line.attributes.some((attribute) => attribute.key === '_zyf_gift_rule'));
      const gift = gifts.find((line) => line.merchandise.id === `gid://shopify/ProductVariant/${expected.variantId}` &&
        Object.entries(expected.properties).every(([key, value]) => line.attributes.some((attribute) => attribute.key === key && attribute.value === value)));
      if (gifts.length !== 1 || !gift || gift.quantity !== 1 || Number(gift.cost.totalAmount.amount) !== 0) {
        throw new Error('The selected checkout could not confirm a free gift. Please try again.');
      }
    }

    // 解析结账 URL 并附加追踪参数
    let checkoutUrl = result.checkoutUrl;
    if (Object.keys(trackingParams).length > 0) {
      try {
        const url = new URL(checkoutUrl);
        Object.entries(trackingParams).forEach(([key, value]) => {
          if (value != null && value !== '') {
            url.searchParams.set(key, String(value));
          }
        });
        checkoutUrl = url.toString();
      } catch (error) {
        console.warn('解析结账 URL 失败，使用原始 URL:', error);
      }
    }

    return checkoutUrl;
  }

  /**
   * 内部方法：构建缓存键
   */
  _buildCacheKey(query, variables) {
    const queryStr = query.replace(/\s+/g, " ").trim();
    const variablesStr = JSON.stringify(variables, Object.keys(variables).sort());
    return `graphql:${btoa(queryStr)}:${btoa(variablesStr)}`;
  }

  /**
   * 清除缓存
   */
  clearCache(pattern = null) {
    if (pattern) {
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
        }
      }
    } else {
      this.cache.clear();
    }
    // console.log("GraphQL 缓存已清除");
  }

  /**
   * 获取缓存统计信息
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }

  /**
   * 取消所有进行中的请求
   */
  abort() {
    this.activeRequests.forEach((controller) => {
      controller.abort();
    });
    this.activeRequests.clear();
  }
}

// 创建全局实例
window.ShopifyStorefrontAPI = new StorefrontAPI();

// 初始化事件系统
(function () {
  let isInitialized = false;
  const initCallbacks = [];

  // 添加初始化完成监听器
  function addInitListener(callback) {
    if (isInitialized) {
      // 如果已经初始化，立即执行回调
      callback();
    } else {
      // 否则添加到队列中
      initCallbacks.push(callback);
    }
  }

  // 触发初始化完成事件
  function triggerInit() {
    if (isInitialized) return;

    isInitialized = true;

    // 执行所有等待的回调
    initCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error("Shopify Storefront 初始化回调执行失败:", error);
      }
    });

    // 清空回调队列
    initCallbacks.length = 0;

    // 派发自定义事件
    const event = new CustomEvent("ShopifyStorefrontAPI:init", {
      detail: {
        api: window.ShopifyStorefrontAPI,
        timestamp: Date.now(),
      },
    });

    document.dispatchEvent(event);

    // console.log("Shopify Storefront API 初始化完成");
  }

  // 检查 DOM 是否已准备好
  function checkDOMReady() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", triggerInit);
    } else {
      // DOM 已经准备好，延迟一点确保其他脚本有时间加载
      setTimeout(triggerInit, 100);
    }
  }

  // 暴露全局方法
  window.ShopifyStorefrontAPI.onInit = addInitListener;

  // 开始检查初始化
  checkDOMReady();
})();
